import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

export const useStyles = makeStyles((theme: Theme) => {
  const pageBackground = theme.palette.grey[400];
  const headerBorder = theme.palette.divider;
  const cellBase = {
    height: "52px !important",
    padding: "0 12px !important",
    boxSizing: "border-box" as const,
    borderTop: "none !important",
    borderLeft: "none !important",
    borderRight: "none !important",
    fontFamily: `${theme.typography.fontFamily} !important`,
    fontSize: `${theme.typography.body2.fontSize} !important`,
    lineHeight: `${theme.typography.body2.lineHeight} !important`,
    textAlign: "right !important" as "right",
  };

  return {
    root: {
      width: "100%",
      backgroundColor: pageBackground,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      border: "none",
      overflow: "visible",
      "& > .MuiPaper-root": {
        width: "100%",
        backgroundColor: "transparent",
        boxShadow: "none",
      },
      "& .MuiTableContainer-root": {
        width: "100%",
        overflow: "visible !important",
      },
      "& .MuiTable-root": {
        width: "100%",
        tableLayout: "fixed",
        borderCollapse: "separate",
        borderSpacing: 0,
        borderBottom: "none !important",
        backgroundColor: `${pageBackground} !important`,
      },
      "& .MuiTableCell-root": {
        whiteSpace: "nowrap",
      },
      '&[dir="rtl"] .MuiTableHead-root .MuiTableCell-head, &[dir="rtl"] .MuiTableBody-root .MuiTableCell-root, &[dir="rtl"] [class*="cellHead"]':
        {
          width: "33.33% !important",
          maxWidth: "33.33% !important",
          minWidth: "33.33% !important",
          padding: "0 12px !important",
          textAlign: "right !important",
          boxSizing: "border-box",
        },
      "& .MuiTableHead-root": {
        opacity: 1,
        borderTop: `1px solid ${headerBorder}`,
        borderBottom: `1px solid ${headerBorder}`,
        borderLeft: `1px solid ${headerBorder}`,
        borderRight: `1px solid ${headerBorder}`,
        borderTopLeftRadius: "4px",
        borderTopRightRadius: "4px",
      },
      "& .MuiTableHead-root .MuiTableRow-root": {
        height: "52px !important",
        opacity: 1,
        backgroundColor: `${pageBackground} !important`,
        borderTop: `1px solid ${headerBorder}`,
        borderBottom: `1px solid ${headerBorder}`,
      },
      "& .MuiTableHead-root .MuiTableCell-head, & [class*='cellHead'], & .MuiTableCell-stickyHeader": {
        ...cellBase,
        height: "52px !important",
        position: "sticky !important",
        top: "0 !important",
        zIndex: 5,
        opacity: 1,
        borderTop: `1px solid ${headerBorder} !important`,
        borderBottom: `1px solid ${headerBorder} !important`,
        borderLeft: "none !important",
        borderRight: "none !important",
        fontWeight: `${theme.typography.fontWeightMedium} !important`,
        backgroundColor: `${pageBackground} !important`,
        color: `${theme.palette.text.primary} !important`,
      },
      "& .MuiTableHead-root .MuiTableCell-head:first-of-type": {
        borderTopLeftRadius: "4px",
        borderLeft: `1px solid ${headerBorder} !important`,
      },
      "& .MuiTableHead-root .MuiTableCell-head:last-of-type": {
        borderTopRightRadius: "4px",
      },
      '&[dir="rtl"] .MuiTableHead-root .MuiTableCell-head:first-of-type, &[dir="rtl"] [class*="cellHead"]:first-of-type':
        {
          borderTopLeftRadius: 0,
          borderTopRightRadius: "4px",
          borderLeft: "none !important",
          borderRight: `1px solid ${headerBorder} !important`,
        },
      '&[dir="rtl"] .MuiTableHead-root .MuiTableCell-head:last-of-type, &[dir="rtl"] [class*="cellHead"]:last-of-type':
        {
          borderTopRightRadius: 0,
          borderTopLeftRadius: "4px",
          borderRight: "none !important",
          borderLeft: `1px solid ${headerBorder} !important`,
        },
      "& .MuiTableBody-root .MuiTableRow-root": {
        height: "52px !important",
        opacity: 1,
        backgroundColor: `${theme.palette.background.paper} !important`,
        "&:hover, &:hover .MuiTableCell-root": {
          backgroundColor: `${theme.palette.background.tableRowHover} !important`,
        },
      },
      "& .MuiTableBody-root .MuiTableCell-root": {
        ...cellBase,
        height: "52px !important",
        opacity: 1,
        borderTop: "none !important",
        borderBottom: `1px solid ${headerBorder} !important`,
        borderLeft: "none !important",
        borderRight: "none !important",
        fontWeight: `${theme.typography.fontWeightRegular} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${theme.palette.secondary.main} !important`,
      },
      "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
        borderRight: `1px solid ${headerBorder} !important`,
      },
      "& .MuiTableBody-root .MuiTableCell-root:last-of-type": {
        borderLeft: `1px solid ${headerBorder} !important`,
      },
      '&[dir="rtl"] .MuiTableBody-root .MuiTableCell-root:first-of-type': {
        borderLeft: "none !important",
        borderRight: `1px solid ${headerBorder} !important`,
      },
      '&[dir="rtl"] .MuiTableBody-root .MuiTableCell-root:last-of-type': {
        borderRight: "none !important",
        borderLeft: `1px solid ${headerBorder} !important`,
      },
    },
    numberCell: {
      display: "block",
      width: "100%",
      textAlign: "right",
      gap: "2px",
      opacity: 1,
    },
    textCell: {
      display: "inline-flex",
      justifyContent: "flex-start",
      width: "100%",
    },
    tableScroll: {
      maxHeight: "calc(100vh - 200px)",
      overflowY: "auto",
      overflowX: "hidden",
      scrollbarWidth: "thin",
      scrollbarColor: `${theme.palette.divider} transparent`,
      "&::-webkit-scrollbar": {
        width: 6,
        display: "block",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.divider,
        borderRadius: 10,
      },
    },
  };
});
